import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Box, Button, IconButton, Modal, TextField, Typography } from '@mui/material';

import asyncService from '../../../../../datahub/asyncService';
import { dispatchErrors } from '../../../../../datahub/errorUtils';
import selectUser from '../../../../store/selectors/appSelector';
import { formatDateOnlyString, parseDateOnlyString } from '../../../../utils';
import { showSnackbar } from '../../../../views/app';
import DatePickerField from '../../../DatePicker/DatePickerField';

import {
  DOB_VALIDATION,
  HTTP_METHODS,
  PII_API_DATA_KEYS,
  PII_API_ROUTES,
  PII_LANGUAGE_KEYS,
  USER_CARD_PII_LANGUAGE_KEYS,
} from './constants';
import {
  formatSsn,
  isSsnSaveDisabled,
  validateDob,
  validateSsn,
  validateSsnMatch,
} from './PersonalIdentifyingInfoModal.helpers';
import styles from './PersonalIdentifyingInfoModal.styles';

const PersonalIdentifyingInfoModal = ({ isOpen, onClose, userId }) => {
  const dispatch = useDispatch();

  const { token, refreshToken } = useSelector(selectUser);

  // State for modal view (initial vs edit)
  const [isEditMode, setIsEditMode] = useState(false);

  // State for data
  const [dateOfBirth, setDateOfBirth] = useState(null);
  const [ssnLastFour, setSsnLastFour] = useState('');
  const [ssn, setSsn] = useState('');
  const [verifySsn, setVerifySsn] = useState('');

  // State for visibility toggles
  const [showSsn, setShowSsn] = useState(false);
  const [showVerifySsn, setShowVerifySsn] = useState(false);

  // State for validation
  const [ssnError, setSsnError] = useState('');
  const [verifySsnError, setVerifySsnError] = useState('');

  // Individual loading states for better UX
  const [ssnLoading, setSsnLoading] = useState(false);
  const [dobLoading, setDobLoading] = useState(false);

  // State for fetch errors
  const [dobFetchError, setDobFetchError] = useState(false);
  const [ssnFetchError, setSsnFetchError] = useState(false);

  // Memoized validation functions for performance
  const validateSsnMemo = useMemo(() => validateSsn, []);
  const validateSsnMatchMemo = useMemo(() => validateSsnMatch, []);

  const fetchPiiData = useCallback(() => {
    setSsnLoading(true);
    setDobLoading(true);
    setDobFetchError(false);
    setSsnFetchError(false);

    // Fetch last 4 SSN digits
    asyncService({
      httpMethod: HTTP_METHODS.POST,
      route: PII_API_ROUTES.SSN_READ,
      data: { [PII_API_DATA_KEYS.USER_ID]: userId },
      token,
      refreshToken,
      dispatch,
      onSuccess: (response) => {
        if (response?.data?.[PII_API_DATA_KEYS.LAST_FOUR]) {
          setSsnLastFour(response.data[PII_API_DATA_KEYS.LAST_FOUR]);
        }
        setSsnLoading(false);
      },
      onError: (d) => {
        setSsnFetchError(true);
        dispatchErrors({ dispatch, responsePayload: d.response });
        setSsnLoading(false);
      },
    });

    // Fetch date of birth
    asyncService({
      httpMethod: HTTP_METHODS.POST,
      route: PII_API_ROUTES.DOB_READ,
      data: { [PII_API_DATA_KEYS.USER_ID]: userId },
      token,
      refreshToken,
      dispatch,
      onSuccess: (response) => {
        if (response?.data?.[PII_API_DATA_KEYS.DOB]) {
          const parsedDate = parseDateOnlyString(response.data[PII_API_DATA_KEYS.DOB]);
          if (parsedDate) {
            setDateOfBirth(parsedDate);
          } else {
            // If date parsing fails, set error but don't clear existing value
            setDobFetchError(true);
          }
        }
        // If no date of birth is returned, don't clear existing value
        setDobLoading(false);
      },
      onError: (d) => {
        setDobFetchError(true);
        // Don't clear DOB field on error to preserve user input
        dispatchErrors({ dispatch, responsePayload: d.response });
        setDobLoading(false);
      },
    });
  }, [dispatch, userId, token, refreshToken]);

  useEffect(() => {
    if (isOpen) {
      // Fetch PII data (last 4 SSN digits and DOB)
      fetchPiiData();
    }
  }, [isOpen, fetchPiiData]);

  const handleSsnChange = (event) => {
    const { value } = event.target;
    // Clean the value to get just digits and set as string
    const cleanValue = value.replace(/\D/g, '').slice(0, 9);
    setSsn(cleanValue);

    const errorKey = validateSsnMemo(cleanValue);
    setSsnError(errorKey || '');

    // Check if it matches the original SSN
    const matchErrorKey = validateSsnMatchMemo(cleanValue, verifySsn);
    setVerifySsnError(matchErrorKey || '');
  };

  const handleVerifySsnChange = (event) => {
    const { value } = event.target;
    // Clean the value to get just digits and set as string
    const cleanValue = value.replace(/\D/g, '').slice(0, 9);
    setVerifySsn(cleanValue);

    // Check if it matches the original SSN
    const matchErrorKey = validateSsnMatchMemo(ssn, cleanValue);
    setVerifySsnError(matchErrorKey || '');
  };

  const handleDateOfBirthChange = (date) => {
    setDateOfBirth(date);
  };

  const getDobHelperText = () => {
    if (dobFetchError) {
      return LanguageConverterV2(USER_CARD_PII_LANGUAGE_KEYS.DOB_FETCH_ERROR);
    }
    if (dobLoading) {
      return 'Loading...';
    }
    return '';
  };

  const fetchFullSsn = useCallback(() => {
    setSsnLoading(true);
    setSsnFetchError(false);

    asyncService({
      httpMethod: HTTP_METHODS.POST,
      route: PII_API_ROUTES.SSN_READ,
      data: { [PII_API_DATA_KEYS.USER_ID]: userId, [PII_API_DATA_KEYS.SSN_FULL_READ]: true },
      token,
      refreshToken,
      dispatch,
      onSuccess: (response) => {
        if (response?.data?.[PII_API_DATA_KEYS.SSN]) {
          // Set SSN directly without formatting
          setSsn(response.data[PII_API_DATA_KEYS.SSN]);
          setVerifySsn(response.data[PII_API_DATA_KEYS.SSN]);
        }
        // If no SSN is returned, don't clear existing values
        setSsnLoading(false);
      },
      onError: (d) => {
        setSsnFetchError(true);
        // Don't clear SSN fields on error to preserve user input
        dispatchErrors({ dispatch, responsePayload: d.response });
        setSsnLoading(false);
      },
    });
  }, [userId, token, refreshToken, dispatch]);

  const handleEditClick = () => {
    setIsEditMode(true);
    // Fetch full SSN when entering edit mode
    fetchFullSsn();
  };

  const handleSsnSave = () => {
    // Validate SSN fields
    const ssnValidationKey = validateSsnMemo(ssn);
    const verifySsnValidationKey = validateSsnMatchMemo(ssn, verifySsn);

    if (ssnValidationKey || verifySsnValidationKey) {
      setSsnError(ssnValidationKey || '');
      setVerifySsnError(verifySsnValidationKey || '');
      return;
    }

    if (!userId) {
      dispatchErrors({ dispatch, customMessage: USER_CARD_PII_LANGUAGE_KEYS.USER_ID_REQUIRED });
      return;
    }

    setSsnLoading(true);

    asyncService({
      httpMethod: HTTP_METHODS.POST,
      route: PII_API_ROUTES.SSN_SET_V2,
      data: { [PII_API_DATA_KEYS.USER_ID]: userId, [PII_API_DATA_KEYS.SSN]: ssn },
      token,
      refreshToken,
      dispatch,
      onSuccess: () => {
        dispatch(
          showSnackbar({ message: USER_CARD_PII_LANGUAGE_KEYS.SSN_UPDATE_SUCCESS, type: 'SUCCESS' })
        );

        setSsnLoading(false);
      },
      onError: (d) => {
        dispatchErrors({ dispatch, responsePayload: d.response });
        setSsnLoading(false);
      },
    });
  };

  const handleDobSave = () => {
    if (!dateOfBirth) {
      return;
    }

    if (!userId) {
      dispatchErrors({ dispatch, customMessage: USER_CARD_PII_LANGUAGE_KEYS.USER_ID_REQUIRED });
      return;
    }

    // Format date as YYYY-MM-DD for API call
    const formattedDob = formatDateOnlyString(dateOfBirth);

    setDobLoading(true);

    asyncService({
      httpMethod: HTTP_METHODS.POST,
      route: PII_API_ROUTES.DOB_SET,
      data: { [PII_API_DATA_KEYS.USER_ID]: userId, [PII_API_DATA_KEYS.DOB]: formattedDob },
      token,
      refreshToken,
      dispatch,
      onSuccess: () => {
        dispatch(
          showSnackbar({ message: USER_CARD_PII_LANGUAGE_KEYS.DOB_UPDATE_SUCCESS, type: 'SUCCESS' })
        );
        setDobLoading(false);
      },
      onError: (d) => {
        dispatchErrors({ dispatch, responsePayload: d.response });
        setDobLoading(false);
      },
    });
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="pii-modal-title"
      aria-describedby="pii-modal-description"
    >
      <Box sx={styles.modal} data-testid="pii-modal">
        <Box sx={styles.header}>
          <Typography id="pii-modal-title" sx={styles.title}>
            {LanguageConverterV2('userCard.personalIdentifyingInfo')}
          </Typography>
          <IconButton onClick={onClose} sx={styles.closeButton} data-testid="pii-close-button">
            <CloseIcon />
          </IconButton>
        </Box>

        {!isEditMode ? (
          // Initial view - show DOB and last 4 SSN digits
          <Box>
            <Box sx={styles.fieldContainer}>
              <Typography sx={styles.label}>
                {LanguageConverterV2(PII_LANGUAGE_KEYS.SSN)}
              </Typography>
              <Box sx={styles.ssnDisplayContainer}>
                {ssnFetchError ? (
                  <Typography sx={{ ...styles.ssnDisplayText, color: 'error.main' }}>
                    {LanguageConverterV2(USER_CARD_PII_LANGUAGE_KEYS.SSN_FETCH_ERROR)}
                  </Typography>
                ) : (
                  <Typography sx={styles.ssnDisplayText}>
                    {ssnLastFour ? `***-**-${ssnLastFour}` : '----'}
                  </Typography>
                )}
                <Button
                  variant="text"
                  onClick={handleEditClick}
                  sx={styles.editButton}
                  data-testid="pii-edit-button"
                >
                  {LanguageConverterV2(PII_LANGUAGE_KEYS.EDIT)}
                </Button>
              </Box>
            </Box>
          </Box>
        ) : (
          // Edit view - show full SSN fields
          <Box>
            <Box sx={styles.fieldContainerSmall}>
              <Typography sx={styles.label}>
                {LanguageConverterV2(PII_LANGUAGE_KEYS.SSN)}
              </Typography>
              <TextField
                value={formatSsn(ssn)}
                onChange={handleSsnChange}
                placeholder="000-00-0000"
                error={!!ssnError}
                helperText={ssnError ? LanguageConverterV2(ssnError) : ''}
                sx={styles.input}
                type={showSsn ? 'text' : 'password'}
                aria-label="Social Security Number"
                aria-describedby="ssn-help-text"
                disabled={ssnLoading}
                InputProps={{
                  inputProps: { 'data-testid': 'pii-ssn-input' },
                  endAdornment: (
                    <IconButton
                      onClick={() => setShowSsn(!showSsn)}
                      sx={styles.visibilityButton}
                      data-testid="pii-ssn-visibility-toggle"
                    >
                      {showSsn ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  ),
                }}
              />
            </Box>

            <Box sx={styles.fieldContainer}>
              <Typography sx={styles.label}>
                {LanguageConverterV2(PII_LANGUAGE_KEYS.VERIFY_SSN)}
              </Typography>
              <Box sx={styles.verifySsnContainer}>
                <TextField
                  value={formatSsn(verifySsn)}
                  onChange={handleVerifySsnChange}
                  placeholder="000-00-0000"
                  error={!!verifySsnError}
                  helperText={verifySsnError ? LanguageConverterV2(verifySsnError) : ''}
                  sx={styles.input}
                  type={showVerifySsn ? 'text' : 'password'}
                  aria-label="Verify Social Security Number"
                  aria-describedby="verify-ssn-help-text"
                  disabled={ssnLoading}
                  InputProps={{
                    inputProps: { 'data-testid': 'pii-verify-ssn-input' },
                    endAdornment: (
                      <IconButton
                        onClick={() => setShowVerifySsn(!showVerifySsn)}
                        sx={styles.visibilityButton}
                        data-testid="pii-verify-ssn-visibility-toggle"
                      >
                        {showVerifySsn ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    ),
                  }}
                />
                <Button
                  variant="outlined"
                  onClick={handleSsnSave}
                  disabled={isSsnSaveDisabled(
                    isEditMode,
                    ssnLoading,
                    ssn,
                    verifySsn,
                    ssnError,
                    verifySsnError
                  )}
                  sx={styles.saveButton}
                  aria-label="Save Social Security Number"
                  data-testid="pii-save-ssn-button"
                >
                  {LanguageConverterV2(PII_LANGUAGE_KEYS.SAVE_SSN)}
                </Button>
              </Box>
            </Box>
          </Box>
        )}
        <Box sx={styles.fieldContainerMedium}>
          <Typography sx={styles.label}>
            {LanguageConverterV2(PII_LANGUAGE_KEYS.DATE_OF_BIRTH)}
          </Typography>
          <Box sx={styles.datePickerContainer}>
            <DatePickerField
              value={dateOfBirth}
              onChange={handleDateOfBirthChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={styles.input}
                  error={dobFetchError}
                  disabled={dobLoading}
                  helperText={getDobHelperText()}
                  inputProps={{
                    ...(params.inputProps || {}),
                    'data-testid': 'pii-dob-input',
                  }}
                />
              )}
              minDate={new Date(DOB_VALIDATION.MIN_DATE)}
              maxDate={DOB_VALIDATION.getMaxDate()}
            />
            <Button
              variant="outlined"
              onClick={handleDobSave}
              disabled={dobLoading || !!validateDob(dateOfBirth)}
              sx={styles.saveButton}
              aria-label="Save Date of Birth"
              data-testid="pii-save-dob-button"
            >
              {LanguageConverterV2(PII_LANGUAGE_KEYS.SAVE)}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

PersonalIdentifyingInfoModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  userId: PropTypes.string.isRequired,
};

export default PersonalIdentifyingInfoModal;
